import * as yup from "yup";
import { BankAccountType } from "../../AddEditNGOPage/const";

export enum OrganizationInputs {
    NAME = "name",
    DESCRIPTION = "description",
    TITLE = "title",
    EMAIL = "email",
    IMAGE = "image",
    CONTACT_PERSON_NAME = "contact_person_name",
    CONTACT_PERSON_EMAIL = "contact_person_email",
    CONTACT_PERSON_MOBILE = "contact_person_mobile"
}

export const organizationSchema = yup.object({
    [OrganizationInputs.IMAGE]: yup.string(),
    [OrganizationInputs.NAME]: yup.string().required("Please enter name"),
    [OrganizationInputs.EMAIL]: yup.string().required("Please enter email"),
    [OrganizationInputs.TITLE]: yup.string(),
    [OrganizationInputs.DESCRIPTION]: yup.string().max(150, "Max 150 characters are allowed"),
    [OrganizationInputs.CONTACT_PERSON_NAME]: yup.string().required("Please enter contact person name"),
    [OrganizationInputs.CONTACT_PERSON_EMAIL]: yup.string().required("Please enter contact person email"),
    [OrganizationInputs.CONTACT_PERSON_MOBILE]: yup.string().required("Please enter contact person mobile"),
})

export interface OrganizationInputTypes {
    [OrganizationInputs.IMAGE]?: string;
    [OrganizationInputs.NAME]: string;
    [OrganizationInputs.EMAIL]: string;
    [OrganizationInputs.TITLE]?: string;
    [OrganizationInputs.DESCRIPTION]?: string;
    [OrganizationInputs.CONTACT_PERSON_NAME]: string;
    [OrganizationInputs.CONTACT_PERSON_EMAIL]: string;
    [OrganizationInputs.CONTACT_PERSON_MOBILE]: string;
}

//version 2 
export enum RegisterOrgInputs {
    NAME = "name",
    TITLE = "title",
    LOGO = "logo",
    DESCRIPTION = "description",
    EMAIL = "email",
    CITIES = "cities",
    TAGLINE = "tagline",
    WEBSITE_URL = "websiteUrl",
    DARPAN_ID = "darpanId",
    DONATION_QR = "donationQr",
    EIGHTY_G_CERTIFICATE = "eightyGCertificate",
    TRUST_DEED_CERTIFICATE = "trustDeedCertificate",
    CONTACT_PERSON_NAME = 'contactPerson.name',
    CONTACT_PERSON_EMAIL = 'contactPerson.email',
    CONTACT_PERSON_NUMBER = 'contactPerson.number',
    CATEGORIES = 'categories',
}

export interface OrganizationInputUpdate {
    [RegisterOrgInputs.NAME]?: string;
    [RegisterOrgInputs.TITLE]?: string;
    [RegisterOrgInputs.LOGO]?: string;
    [RegisterOrgInputs.DESCRIPTION]?: string;
    [RegisterOrgInputs.EMAIL]?: string;
    [RegisterOrgInputs.CITIES]?: string[];
    [RegisterOrgInputs.TAGLINE]?: string;
    [RegisterOrgInputs.WEBSITE_URL]?: string;
    [RegisterOrgInputs.DARPAN_ID]?: string;
    [RegisterOrgInputs.EIGHTY_G_CERTIFICATE]?: string;
    [RegisterOrgInputs.DONATION_QR]?: string;
    [RegisterOrgInputs.TRUST_DEED_CERTIFICATE]?: string;
    [RegisterOrgInputs.CONTACT_PERSON_NAME]?: string;
    [RegisterOrgInputs.CONTACT_PERSON_EMAIL]?: string;
    [RegisterOrgInputs.CONTACT_PERSON_NUMBER]?: string;
    [RegisterOrgInputs.CATEGORIES]?: string[];
    bankDetails?: {
        bankName?: string;
        branch?: string;
        ifscCode?: string;
        accountType?: BankAccountType;
        beneficiaryName?: string;
        accountNumber?: string;
        upiId?: string;
    };
}

const bankDetailsSchema = {
    bankName: yup.string().trim().max(100).required("Bank name is required"),
    branch: yup.string().trim().max(100).required("Branch name is required"),
    ifscCode: yup
        .string()
        .trim()
        .required("IFSC code is required"),
    accountType: yup
        .string()
        .oneOf([BankAccountType.SAVINGS, BankAccountType.CURRENT])
        .required("Account type is required"),

    beneficiaryName: yup.string().trim().max(120).required("Beneficiary name is required"),
    accountNumber: yup
        .string()
        .trim()
        .matches(/^\d{9,18}$/, "Account number must be 9-18 digits")
        .required("Account number is required"),
    upiId: yup.string().trim().max(50).optional(),
};

export const registerOrganizationSchema = yup.object({
    name: yup.string().required("Name is required"),
    title: yup.string().required("Title is required"),
    description: yup
        .string()
        .required("Description is required")
        .max(150, "Max 150 characters are allowed"),
    email: yup
        .string()
        .email("Invalid organization email")
        .required("Organization email is required"),
    tagline: yup.string().required("Tagline is required"),
    websiteUrl: yup.string().url("Invalid URL").optional(),
    darpanId: yup.string().required("Darpan ID is required"),
    logo: yup.mixed<File>().nullable().optional(),
    donationQr: yup.mixed<File>().nullable().optional(),
    eightyGCertificate: yup.mixed<File>().nullable().required("80G Certificate is required"),
    trustDeedCertificate: yup.mixed<File>().nullable().required("Trust Deed Certificate is required"),
    cities: yup
        .array()
        .of(yup.string().required())
        .min(1, "Select at least one city")
        .required("Select at least one city"),
    contact_person: yup.object({
        name: yup.string().required("Contact person name is required"),
        email: yup
            .string()
            .email("Invalid email")
            .required("Contact person email is required"),
        number: yup.string().optional(),
    }).required(),
    categories: yup.array().of(yup.string().required()).optional(),
    bankDetails: yup.object(bankDetailsSchema).required("Bank details are required"),
});

export const updateOrgSchema = yup.object({
    name: yup.string().required("Name is required"),
    title: yup.string().required("Title is required"),
    description: yup
        .string()
        .max(150, "Max 150 characters are allowed")
        .required("Description is required"),
    email: yup
        .string()
        .email("Invalid organization email")
        .required("Organization email is required"),
    tagline: yup.string().required("Tagline is required"),
    websiteUrl: yup.string().url("Invalid URL").optional(),
    darpanId: yup.string().required("Darpan ID is required"),
    cities: yup
        .array()
        .of(yup.string().required())
        .min(1, "Select at least one city")
        .required("Select at least one city"),
    logo: yup.mixed<File>().nullable().optional(),
    eightyGCertificate: yup.mixed<File>().nullable().optional(),
    trustDeedCertificate: yup.mixed<File>().nullable().optional(),
    categories: yup.array().of(yup.string().required()).optional(),
    donationQr: yup.mixed<File>().nullable().optional(),
    bankDetails: yup.object({
        ...bankDetailsSchema,
        bankName: bankDetailsSchema.bankName.optional(),
        branch: bankDetailsSchema.branch.optional(),
        ifscCode: bankDetailsSchema.ifscCode.optional(),
        accountType: bankDetailsSchema.accountType.optional(),
        beneficiaryName: bankDetailsSchema.beneficiaryName.optional(),
        accountNumber: bankDetailsSchema.accountNumber.optional(),
        upiId: bankDetailsSchema.upiId,
    }).optional(),
});


export type OrganizationFormValues = {
    name: string;
    title: string;
    description: string;
    email: string;
    tagline: string;
    websiteUrl?: string;
    darpanId: string;
    logo?: File | null;
    donationQr?: File | null;
    eightyGCertificate?: File | null;
    trustDeedCertificate?: File | null;
    cities: string[];
    contact_person?: {
        name?: string;
        email?: string;
        number?: string;
    };
    categories?: string[];
    bankDetails?: {
        bankName?: string;
        branch?: string;
        ifscCode?: string;
        accountType?: BankAccountType;
        beneficiaryName?: string;
        accountNumber?: string;
        upiId?: string;
    };
};

export type RegisterOrganizationInput = yup.InferType<typeof registerOrganizationSchema>;
export type UpdateOrganizationInput = yup.InferType<typeof updateOrgSchema>;

export enum OrgFormDataFields {
    NAME = "name",
    TITLE = "title",
    DESCRIPTION = "description",
    EMAIL = "email",
    TAGLINE = "tagline",
    WEBSITE_URL = "websiteUrl",
    DARPAN_ID = "darpanId",
    CITIES = "cities[]",
    CONTACT_NAME = "contact_person[name]",
    CONTACT_EMAIL = "contact_person[email]",
    CONTACT_NUMBER = "contact_person[number]",
    LOGO = "logo",
    EIGHTY_G_CERTIFICATE = "eightyGCertificate",
    TRUST_DEED_CERTIFICATE = "trustDeedCertificate",
    CATEGORIES = "categories[]",
    BANK_NAME = "bankDetails.bankName",
    BANK_BRANCH = "bankDetails.branch",
    IFSC_CODE = "bankDetails.ifscCode",
    ACCOUNT_TYPE = "bankDetails.accountType",
    BENEFICIARY_NAME = "bankDetails.beneficiaryName",
    ACCOUNT_NUMBER = "bankDetails.accountNumber",
    UPI_ID = "bankDetails.upiId",
    DONATION_QR = "donationQr",
}